import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { OverrideDialogComponent } from '../../Dialogs/override-dialog/override-dialog.component';
import { SubscriptionConfirmDialogComponent } from '../../Dialogs/subscription-confirm-dialog/subscription-confirm-dialog.component';
import { SubscriptionControlFacade } from '../../../../../Facades/Subscriptions/subscription-control.facade';
import { DATE_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  CONFIRM_BUTTON,
  OVERRIDE_REVERT_AMOUNT_KEY,
  OVERRIDE_REVERT_CONFIRM_TITLE,
  OVERRIDE_REVERT_CONSEQUENCE,
} from '../../../../../Consts/subscription-control.const';
import {
  PRICE_COMPONENT_LABEL,
  PRICE_COMPONENT_PRODUCT_NAME,
  PriceComponentType,
} from '../../../../../Enums/price-component-type.enum';
import { SubscriptionOverride } from '../../../../../Models/backoffice/subscription-override.model';
import {
  OverrideDialogResult,
  SubscriptionConfirmDialogResult,
} from '../../../Interfaces/subscription-dialog.interface';

@Component({
  selector: 'av-subscription-items',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    AvEmptyStateComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './subscription-items.component.html',
  styleUrl: './subscription-items.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class SubscriptionItemsComponent {
  private readonly _dialog = inject(MatDialog);
  private readonly _translate = inject(TranslateService);

  readonly facade = inject(SubscriptionControlFacade);

  readonly organizationName = input<string>('');

  readonly dateFormat = DATE_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly componentLabelKeys = PRICE_COMPONENT_LABEL;
  readonly productNames = PRICE_COMPONENT_PRODUCT_NAME;

  readonly activeOverride = computed<SubscriptionOverride | null>(() => {
    const raw = this.facade.selectedItem()?.active_override ?? null;

    return raw === null ? null : new SubscriptionOverride(raw);
  });

  componentLabelKey(componentType: string): string {
    return this.componentLabelKeys[componentType as PriceComponentType] ?? componentType;
  }

  productName(componentType: string): string {
    return this.productNames[componentType as PriceComponentType] ?? componentType;
  }

  selectItem(itemId: string): void {
    this.facade.selectItem(itemId);
  }

  openOverride(): void {
    const item = this.facade.selectedItem();

    if (item === null) {
      return;
    }

    const reference = this._dialog.open(OverrideDialogComponent, {
      data: {
        componentLabelKey: this.componentLabelKey(item.component_type),
        organizationName: this.organizationName(),
        currentUnitAmount: item.unit_amount,
        currency: item.currency,
        hasActiveOverride: item.active_override !== null,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: OverrideDialogResult) => this._applyOverride(result));
  }

  openRevert(override: SubscriptionOverride): void {
    const reference = this._dialog.open(SubscriptionConfirmDialogComponent, {
      data: {
        title: OVERRIDE_REVERT_CONFIRM_TITLE,
        consequence: OVERRIDE_REVERT_CONSEQUENCE,
        organizationName: this.organizationName(),
        amountLabel: this._translate.instant(OVERRIDE_REVERT_AMOUNT_KEY, {
          amount: override.override_unit_amount,
          currency: override.currency ?? '',
          original: override.original_unit_amount,
        }),
        confirmLabel: CONFIRM_BUTTON,
        isCommitmentLocked: false,
        canOverrideCommitment: false,
        modes: [],
        modeLabels: {},
        modeConsequences: {},
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: SubscriptionConfirmDialogResult) =>
        this._revertOverride(override.id, result),
      );
  }

  private _applyOverride(result?: OverrideDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.applyOverride({
      override_unit_amount: result.amount,
      reason: result.reason,
      ends_at: result.endsAt,
    });
  }

  private _revertOverride(overrideId: string, result?: SubscriptionConfirmDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.revertOverride(overrideId, result.reason);
  }
}
